import {useEffect, useRef, useState} from 'react';
import {ScrollToSlideOptions} from '../../components/molecules/HorizontalSlider/types';

interface useAutomaticSlideProps {
  slidesLength: number;
  currentSlide: number;
  scrollToSlide: (position: number, options?: ScrollToSlideOptions) => void;
  slideDurationInSecs?: number;
  stoppedInitialState?: boolean;
}

export const useAutomaticSlide = ({
  slidesLength,
  currentSlide,
  scrollToSlide,
  slideDurationInSecs = 5,
  stoppedInitialState = false,
}: useAutomaticSlideProps) => {
  const [transitionSlideId, setTransitionSlideId] = useState<number>(-1);
  const [animationPaused, setAnimationPaused] = useState(false);
  const [animationStopped, setAnimationStopped] = useState(stoppedInitialState);

  const timerRef = useRef<
    | {
        timer: NodeJS.Timeout | undefined;
        startTime: number;
        remaining?: number;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    if (currentSlide === transitionSlideId) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setTransitionSlideId(-1);
    }
  }, [currentSlide, transitionSlideId]);

  const changeSlide = () => {
    if (animationStopped) {
      return;
    }

    const newIndex = currentSlide + 1;
    const canSlideNext = newIndex < slidesLength;

    if (!canSlideNext) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setTransitionSlideId(0);
    }

    scrollToSlide(canSlideNext ? newIndex : 0);
  };

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    clearTimeout(timerRef.current?.timer);

    if (animationStopped) {
      return;
    }

    const slideInterval = setTimeout(changeSlide, slideDurationInSecs * 1000);
    timerRef.current = {
      timer: slideInterval,
      startTime: new Date().getTime(),
    };
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current.timer);
      }
    };
  }, [slidesLength, currentSlide, scrollToSlide, animationStopped]);

  const onPause = () => {
    if (timerRef.current) {
      const cardDuration = slideDurationInSecs * 1000;
      clearTimeout(timerRef.current.timer);
      const remaining =
        cardDuration - (new Date().getTime() - timerRef.current.startTime);
      timerRef.current = {
        timer: undefined,
        startTime: timerRef.current.startTime,
        remaining,
      };
    }
    setAnimationPaused(true);
  };

  const onResume = () => {
    if (timerRef.current) {
      timerRef.current = {
        timer: setTimeout(
          changeSlide,
          timerRef.current.remaining || slideDurationInSecs * 1000,
        ),
        startTime: new Date().getTime(),
      };
    }
    setAnimationPaused(false);
  };

  const onStop = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current.timer);
    }

    timerRef.current = {
      timer: undefined,
      startTime: 0,
      remaining: 0,
    };
    setAnimationStopped(true);
  };

  const onReset = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current.timer);
    }

    const slideInterval = setTimeout(changeSlide, slideDurationInSecs * 1000);
    timerRef.current = {
      timer: slideInterval,
      startTime: new Date().getTime(),
    };
    setAnimationStopped(false);
    setAnimationPaused(false);
  };

  return {
    onResume,
    onPause,
    onStop,
    animationPaused,
    animationStopped,
    onReset,
  };
};
